import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import DateRangeSelector from "../common/DateRangeSelector";
import AdminScopeFilters, {
  ALL_BUSINESS_TYPES,
  ALL_COUNTRIES,
  AdminScopeFilterLegend,
} from "../common/AdminScopeFilters";
import AdminActiveFilterChips from "../common/AdminActiveFilterChips";
import AdminPageShell from "../common/AdminPageShell";
import AdminFilterBar from "../common/AdminFilterBar";
import AdminDashboardBreakdown from "./AdminDashboardBreakdown";
import { format } from "date-fns";
import { useAdminDashboardAnalytics } from "../../hooks/api/useAdminAnalytics";
import { formatRevenueByCurrency } from "../common/adminMarketCountries";
import { adminKpiCard } from "../../styles/adminUiTokens";
import {
  buildDashboardRows,
  hasBreakdownData,
  summarizeDashboardRows,
} from "./dashboardAnalyticsUtils";

const AdminDashboardPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);
  const [countryCode, setCountryCode] = useState(ALL_COUNTRIES);
  const [businessType, setBusinessType] = useState(ALL_BUSINESS_TYPES);
  const analytics = useAdminDashboardAnalytics();

  const scopeFilters = { countryCode, businessType };
  const hasScopeFilter =
    countryCode !== ALL_COUNTRIES || businessType !== ALL_BUSINESS_TYPES;

  useEffect(() => {
    const input: {
      dateFrom?: string;
      dateTo?: string;
      countryCode?: string;
      businessType?: string;
    } = {};
    if (dateRange) {
      input.dateFrom = format(dateRange.startDate, "yyyy-MM-dd");
      input.dateTo = format(dateRange.endDate, "yyyy-MM-dd");
    }
    if (countryCode) input.countryCode = countryCode;
    if (businessType) input.businessType = businessType;
    analytics.mutate(Object.keys(input).length > 0 ? input : undefined);
  }, [dateRange, countryCode, businessType]);

  const company = analytics.data?.getAdminDashboardAnalytics?.companyAnalytics;
  const sub = analytics.data?.getAdminDashboardAnalytics?.subscriptionAnalytics;
  const pay = analytics.data?.getAdminDashboardAnalytics?.paymentAnalytics;
  const superAdmin =
    analytics.data?.getAdminDashboardAnalytics?.superAdminAnalytics;
  const code = analytics.data?.getAdminDashboardAnalytics?.code;

  const breakdownRows = useMemo(
    () =>
      buildDashboardRows(
        company?.byBusinessType,
        pay?.byBusinessType,
        sub?.byBusinessType,
        countryCode || undefined,
        businessType || undefined,
      ),
    [company, pay, sub, countryCode, businessType],
  );

  const scopedSummary = useMemo(
    () => summarizeDashboardRows(breakdownRows),
    [breakdownRows],
  );

  const breakdownAvailable = hasBreakdownData(
    company?.byBusinessType,
    pay?.byBusinessType,
    sub?.byBusinessType,
  );

  const useScopedKpis = hasScopeFilter && breakdownAvailable;

  const companiesTotal = useScopedKpis
    ? scopedSummary.companiesTotal
    : (company?.totalCompanies ?? null);
  const companiesActive = useScopedKpis
    ? scopedSummary.companiesActive
    : (company?.activeCompanies ?? null);
  const subscriptionsActive = useScopedKpis
    ? scopedSummary.subscriptionsActive
    : (sub?.activeSubscriptions ?? null);
  const subscriptionsTotal = useScopedKpis
    ? scopedSummary.subscriptionsTotal
    : (sub?.totalSubscriptions ?? null);
  const revenueLabel = useScopedKpis
    ? formatRevenueByCurrency(scopedSummary.revenueByCurrency)
    : pay != null
      ? formatRevenueByCurrency([{ currency: "RWF", amount: pay.totalRevenue }])
      : null;
  const completedPayments = useScopedKpis
    ? scopedSummary.completedPayments
    : (pay?.totalPaymentsCount ?? null);

  const kpiCards = [
    {
      title: "Companies",
      value:
        companiesTotal != null
          ? String(companiesTotal)
          : analytics.isPending
            ? "…"
            : "—",
      subtitle:
        companiesActive != null ? `${companiesActive} active` : undefined,
    },
    {
      title: "Subscriptions",
      value:
        subscriptionsActive != null
          ? String(subscriptionsActive)
          : analytics.isPending
            ? "…"
            : "—",
      subtitle:
        subscriptionsTotal != null ? `${subscriptionsTotal} total` : undefined,
    },
    {
      title: "Revenue",
      value: revenueLabel ?? (analytics.isPending ? "…" : "—"),
      subtitle:
        completedPayments != null
          ? `${completedPayments} completed payments`
          : undefined,
    },
    {
      title: "Super admins",
      value:
        superAdmin != null
          ? String(superAdmin.activeSuperAdmins)
          : analytics.isPending
            ? "…"
            : "—",
      subtitle:
        superAdmin != null ? `${superAdmin.totalSuperAdmins} total` : undefined,
    },
  ];

  const clearAllScope = () => {
    setCountryCode(ALL_COUNTRIES);
    setBusinessType(ALL_BUSINESS_TYPES);
  };

  const alerts = (
    <>
      {hasScopeFilter && !breakdownAvailable && code === "OK" && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Industry and country filters apply to the breakdown tables below once
          Identity and Membership analytics are deployed. Top-level counts
          remain platform-wide.
        </Alert>
      )}
      {dateRange && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 2 }}
        >
          Period filter applies to subscription and payment activity metrics on
          the server; company counts are always all-time.
        </Typography>
      )}
      {analytics.isPending && !analytics.data ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 4 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Loading analytics…
          </Typography>
        </Box>
      ) : code !== "OK" ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Analytics unavailable ({code ?? "error"}). Check that Identity and
          Membership services are reachable.
        </Alert>
      ) : null}
    </>
  );

  return (
    <AdminPageShell
      title="Dashboard"
      description={<AdminScopeFilterLegend />}
      toolbar={
        <AdminFilterBar
          filters={
            <AdminScopeFilters
              values={scopeFilters}
              onChange={({ countryCode: c, businessType: b }) => {
                setCountryCode(c);
                setBusinessType(b);
              }}
              periodControl={
                <DateRangeSelector
                  compact
                  value={dateRange}
                  onChange={setDateRange}
                />
              }
            />
          }
        />
      }
      filterChips={
        <AdminActiveFilterChips
          values={scopeFilters}
          onClearCountry={() => setCountryCode(ALL_COUNTRIES)}
          onClearBusinessType={() => setBusinessType(ALL_BUSINESS_TYPES)}
          onClearAll={clearAllScope}
        />
      }
      alerts={alerts}
    >
      {code === "OK" && (analytics.data || !analytics.isPending) && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 1.5, sm: 2 },
              mb: 3,
            }}
          >
            {kpiCards.map((kpi) => (
              <Card key={kpi.title} elevation={0} sx={adminKpiCard}>
                <CardContent sx={{ py: 2, px: 2.5, "&:last-child": { pb: 2 } }}>
                  <Typography variant="body2" color="text.secondary">
                    {kpi.title}
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{ mt: 0.5, wordBreak: "break-word" }}
                  >
                    {kpi.value}
                  </Typography>
                  {kpi.subtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {kpi.subtitle}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          {breakdownAvailable ? (
            <AdminDashboardBreakdown
              rows={breakdownRows}
              showCountryDetail={!countryCode}
            />
          ) : (
            code === "OK" && (
              <Alert severity="info">
                Industry breakdown requires the latest Identity and Membership
                deployments. Until then, use Companies, Subscriptions, and
                Payments lists with country and industry filters.
              </Alert>
            )
          )}
        </>
      )}
    </AdminPageShell>
  );
};

export default AdminDashboardPage;
